import { DeployRefreshPrompt } from "@pathwright/features/src/modules/auth/heartbeat/DeployRefreshPrompt"
import useHeartbeat from "@pathwright/features/src/modules/auth/heartbeat/useHeartbeat"
import { BackgroundTaskContextProvider } from "@pathwright/features/src/modules/bg-task/BackgroundTaskContext"
import StripeAlert from "@pathwright/features/src/modules/commerce/manage/stripe/StripeAlert"
import CurrencyFormatterProvider from "@pathwright/features/src/modules/currency/CurrencyFormatterProvider"
import IntercomProvider, {
  useIntercomContext
} from "@pathwright/features/src/modules/intercom/IntercomProvider"
import ErrorParamAlert from "@pathwright/features/src/modules/lib/ErrorParamAlert"
import { FirebaseProvider } from "@pathwright/features/src/modules/lib/PathwrightFirebaseProvider"
import { AlgoliaProvider } from "@pathwright/features/src/modules/library/browse/AlgoliaProvider"
import I18nextProvider from "@pathwright/features/src/modules/lng/I18nextProvider"
import {
  ModalContextProvider,
  ModalPortal
} from "@pathwright/features/src/modules/modal"
import { NotificationsProvider } from "@pathwright/features/src/modules/notifications/state/notification-state"
import GlobalSchoolThemeStyles from "@pathwright/features/src/modules/pathwright/GlobalSchoolThemeStyles"
import {
  PathwrightClientContextProvider,
  usePathwrightContext
} from "@pathwright/features/src/modules/pathwright/PathwrightContext"
import StagingPreviewAlert from "@pathwright/features/src/modules/pathwright/StagingPreviewAlert"
import PWAdminCmdK from "@pathwright/features/src/modules/pw-admin/PWAdminCmdK"
import { PWAdminCmdKContextProvider } from "@pathwright/features/src/modules/pw-admin/PWAdminCmdKContext"
import useReferral from "@pathwright/features/src/modules/referral/useReferral"
import SpaceAugmentationDrawer from "@pathwright/features/src/modules/space/augmentation/SpaceAugmentationDrawer"
import SubscriptionsNeedingAction from "@pathwright/features/src/modules/subscription/SubscriptionsNeedingAction"
import ChakraThemeProvider from "@pathwright/features/src/modules/theme/ChakraThemeProvider"
import { useCanViewSimpleNav } from "@pathwright/features/src/modules/unified-home/view-permission/useUnifiedHomePermissions"
import UserMembershipAccessExpired from "@pathwright/features/src/modules/user/UserMembershipAccessExpired"
import AlertBanner from "@pathwright/ui/src/components/alert/AlertBanner"
import PopupAlert from "@pathwright/ui/src/components/alert/PopupAlert"
import ErrorBoundary from "@pathwright/ui/src/components/error/ErrorBoundary"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import ObserveSizeContext, {
  ObserveSizeProvider
} from "@pathwright/ui/src/components/observers/ObserveSizeContext"
import DockPanel from "@pathwright/ui/src/components/panel/DockPanel"
import ScrollView from "@pathwright/ui/src/components/scroll/ScrollView"
import PathwrightUI from "@pathwright/ui/src/components/ui/PathwrightUI"
import Screensize from "@pathwright/ui/src/components/ui/Screensize"
import { useApolloOperationErrorToast } from "@pathwright/utils/src/graphql/apolloOperation"
import { Toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import SentryError from "@pathwright/ux/src/components/error/SentryError"
import Togglei18nKeys from "@pathwright/ux/src/components/lng/Togglei18nKeys"
import assign from "lodash/assign"
import React, { Suspense, useEffect, useMemo } from "react"
import { HotkeysProvider } from "react-hotkeys-hook"
import { useAuthSync } from "./ClientRoutes"
import { AppNavbar, AppPanel } from "./lib/core/components/nav"
import { OverlaySlot } from "./lib/core/routing/OverlaySlots"
import { useHistoryEvent } from "./lib/core/routing/router"
import { ConnectStores } from "./lib/core/store"
import "./lib/core/styles/App.css"
import "./lib/core/styles/OverrideStyles.css"

const WrappedSentryError = (props) => {
  const { me } = usePathwrightContext()
  return <SentryError {...props} user={me} />
}

// Toggles Intercom UI visibility based on DockPanel layout.
const WrappedDockPanel = (props) => {
  const { hideIntercomUI, showIntercomUI } = useIntercomContext()

  return (
    <DockPanel
      {...props}
      onChangeLayout={({ open, docked }) => {
        // Whenever DockPanel is open and not docked, hide the Intercom launcher, etc.
        if (open && !docked) hideIntercomUI()
        // Whenever DockPanel is closed or docked, show the Intercom launcher, etc.
        if (!open || docked) showIntercomUI()
      }}
    />
  )
}

const App = (props) => {
  const { children, authSync, schoolId, panel, primary, popupAlert, aside } =
    props

  const pwContext = usePathwrightContext()
  const operationErrorModal = useApolloOperationErrorToast()
  const { me } = pwContext
  useHistoryEvent()
  useHeartbeat()
  useReferral()

  const canViewUnifiedHome = useCanViewSimpleNav()

  // Sync school's features in Pathwright context to legacy school store.
  useEffect(() => {
    window.App.getStore("school")._mutate("features", pwContext.school.features)
  }, [pwContext.school])

  const panelProps = React.useMemo(
    () =>
      panel
        ? {
            ...(panel.config || {}),
            children: panel.Component ? (
              <panel.Component {...(panel.props || {})} />
            ) : null
          }
        : {},
    [panel]
  )
  return (
    <>
      {typeof children === "function" ? children({ authSync }) : children}
      <ModalContextProvider>
        <ObserveSizeContext.Consumer>
          {({ getRectValue }) => (
            <>
              {/* {inPortal} */}
              {/* Optionally portal something like a ThemeBackground */}
              <div id="theme-portal" />
              <div
                className="App"
                style={{
                  top: getRectValue("alert.height"),
                  right: getRectValue("cardstack.width")
                }}
              >
                <ScrollView fill className="App__primary">
                  {canViewUnifiedHome === false ? (
                    <>
                      <AppPanel user={me} />
                      <AppNavbar user={me} />
                    </>
                  ) : (
                    <SpaceAugmentationDrawer />
                  )}
                  <ErrorBoundary className="PrimaryErrorBoundary">
                    <div className="App__primary__component">{primary}</div>
                  </ErrorBoundary>
                </ScrollView>
                {/* TODO: merge assid into card stack? */}
                {!!aside && (
                  <aside.Component className="App__aside" {...aside.props} />
                )}
              </div>
              <WrappedDockPanel {...panelProps} />
              <OverlaySlot slot="modal" style={{ display: "contents" }} />
              {operationErrorModal}
              {!!popupAlert && <PopupAlert {...popupAlert.props} />}
              <Togglei18nKeys />
              <GlobalSchoolThemeStyles />
              <StripeAlert.Reconnect />
              <AlertBanner.Container />
              <StagingPreviewAlert />
              <ErrorParamAlert />
              <SubscriptionsNeedingAction />
              <UserMembershipAccessExpired />
              <ModalPortal />
              <div id="fullscreen-portal">
                <div id="fullscreen-theme-portal" />
              </div>
              {/* Putting cards on top of everything */}
              <OverlaySlot slot="cards" className="App__cards" />
              <PWAdminCmdK />
              <DeployRefreshPrompt />
              <Toaster />
            </>
          )}
        </ObserveSizeContext.Consumer>
      </ModalContextProvider>
    </>
  )
}

App.displayName = "App"

// Connect AppLecacyContext to various stores
let connectedStores = [
  { store: "navigation", watch: ["panel"] },
  { store: "school", watch: ["integrations", "listen_hack"] },
  { store: "layout", watch: ["modal", "panel", "popupAlert", "aside"] }
]

const AppMapStateToProps = function (state, ownProps) {
  const { school, integrations, media, permissions } = state.schoolStore.state
  const { panel, popupAlert, aside } = state.layoutStore.state

  return assign({}, ownProps, {
    school,
    media,
    panel,
    popupAlert,
    aside,
    permissions,
    integrations
  })
}

const AppConnected = ConnectStores(App, connectedStores, {}, AppMapStateToProps)

// The provided uiTheme is dependent on bootstrapped data, but we want the school
// theme to be dependent on the pathwright context data, hence this extra merge layer.
export const SchoolThemeProvider = ({ uiTheme: uiThemeProp, children }) => {
  const { school } = usePathwrightContext()

  const uiTheme = useMemo(
    () =>
      school.theme
        ? {
            ...uiThemeProp,
            primaryBrandColor:
              school.theme.primary_color || uiThemeProp.primaryBrandColor,
            backgroundImage:
              school.theme.background_image || uiThemeProp.backgroundImage,
            backgroundOverlay:
              school.theme.background_overlay || uiThemeProp.backgroundOverlay,
            headingFont: school.theme.heading_font
          }
        : uiThemeProp,
    [school.theme, uiThemeProp]
  )

  return (
    <PathwrightUI.Provider theme={uiTheme}>{children}</PathwrightUI.Provider>
  )
}

// Connect PathwrightWeb client, PathwrightUI theme
const PathwrightApp = (props) => {
  const { schoolId, is_authenticated, theme, logo, icon } = props

  const uiTheme = useMemo(
    () => ({
      ...PathwrightUI.Provider.defaultTheme,
      primaryBrandColor:
        theme.primary_color ||
        PathwrightUI.Provider.defaultTheme.primaryBrandColor,
      backgroundImage:
        theme.background_image ||
        PathwrightUI.Provider.defaultTheme.backgroundImage,
      backgroundOverlay:
        theme.background_overlay ||
        PathwrightUI.Provider.defaultTheme.backgroundOverlay,
      headingFont: theme.heading_font,
      logo,
      icon
    }),
    [theme, logo, icon]
  )

  const authSync = useAuthSync()

  return (
    <Suspense fallback={<LoadingCircle />}>
      <PathwrightClientContextProvider
        authenticated={is_authenticated}
        schoolId={schoolId}
        onAuthChange={authSync}
      >
        <I18nextProvider useLocalTranslation>
          <SchoolThemeProvider uiTheme={uiTheme}>
            <ChakraThemeProvider>
              <WrappedSentryError>
                <ErrorBoundary className="AppErrorBoundary">
                  <FirebaseProvider>
                    <Screensize.Provider>
                      <CurrencyFormatterProvider>
                        <IntercomProvider>
                          <AlgoliaProvider>
                            <NotificationsProvider>
                              <ObserveSizeProvider>
                                <BackgroundTaskContextProvider>
                                  <HotkeysProvider>
                                    <PWAdminCmdKContextProvider>
                                      <AppConnected
                                        {...props}
                                        authSync={authSync}
                                      />
                                    </PWAdminCmdKContextProvider>
                                  </HotkeysProvider>
                                </BackgroundTaskContextProvider>
                              </ObserveSizeProvider>
                            </NotificationsProvider>
                          </AlgoliaProvider>
                        </IntercomProvider>
                      </CurrencyFormatterProvider>
                    </Screensize.Provider>
                  </FirebaseProvider>
                </ErrorBoundary>
              </WrappedSentryError>
            </ChakraThemeProvider>
          </SchoolThemeProvider>
        </I18nextProvider>
      </PathwrightClientContextProvider>
    </Suspense>
  )
}

PathwrightApp.displayName = "PathwrightApp"

const PathwrightAppMapStateToProps = function (state, ownProps) {
  let { is_authenticated } = state.authStore.state
  let { school, media, integrations } = state.schoolStore.state

  return assign(
    {},
    {
      integrations,
      is_authenticated,
      schoolId: school.id,
      theme: media.theme,
      logo: media.logo,
      icon: media.image
    },
    ownProps
  )
}

const PathwrightAppConnected = ConnectStores(
  PathwrightApp,
  [
    { store: "auth", watch: ["is_authenticated"] },
    {
      store: "school",
      watch: ["school", "media", "integrations", "listen_hack"]
    }
  ],
  {},
  PathwrightAppMapStateToProps
)

export default PathwrightAppConnected
